import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Users, Heart, Dumbbell, Scale, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { IMAGES } from '../mock/mock';

const INSTAGRAM = '@samturner_fitness_and_family';

function ChapterHero() {
  return (
    <section className="relative overflow-hidden bg-black">
      <div className="absolute inset-0">
        <img src={IMAGES.samHero} alt="" className="w-full h-full object-cover object-right opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/20" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 font-semibold">01 / 05</div>
          <div className="mt-3 text-[11px] tracking-[0.35em] uppercase text-brand font-bold">ST Fitness · Manchester</div>
          <h1 className="mt-5 font-display italic text-white uppercase text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
            My Story.<br /><span className="text-brand">My Mission.</span>
          </h1>
          <div className="mt-6 h-1 w-20 bg-brand" />
          <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-2xl">
            <div className="flex items-center gap-3 bg-white/[0.04] border border-white/10 px-4 py-3">
              <Calendar className="w-5 h-5 text-brand" />
              <div className="leading-tight">
                <div className="text-white font-semibold">10 Years</div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest">of coaching</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/[0.04] border border-white/10 px-4 py-3">
              <Users className="w-5 h-5 text-brand" />
              <div className="leading-tight">
                <div className="text-white font-semibold">3 Children</div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest">family first</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/[0.04] border border-white/10 px-4 py-3">
              <Heart className="w-5 h-5 text-brand" />
              <div className="leading-tight">
                <div className="text-white font-semibold">One Purpose</div>
                <div className="text-white/50 text-[10px] uppercase tracking-widest">real change</div>
              </div>
            </div>
          </div>
          <p className="mt-8 text-white/80 text-lg max-w-2xl leading-relaxed">
            Helping women feel <span className="text-brand font-semibold">strong, confident and capable</span> in every part of life.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <div className="text-brand font-semibold uppercase tracking-widest text-xs">Sam Turner</div>
            <div className="w-8 h-px bg-white/30" />
            <div className="text-white/60 uppercase tracking-widest text-xs">Personal Trainer & Coach</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChapterHuman() {
  const tags = [
    { icon: Users, label: 'Mum of 3' },
    { icon: Dumbbell, label: 'Business owner' },
    { icon: Heart, label: 'Coach' },
    { icon: Heart, label: 'Wife' },
  ];
  return (
    <section className="relative py-24 bg-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 reveal order-2 lg:order-1">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 font-semibold">02 / 05</div>
          <div className="mt-3 text-[11px] tracking-[0.35em] uppercase text-brand font-bold">Who I Am</div>
          <h2 className="mt-4 font-display italic text-white uppercase text-5xl md:text-6xl leading-[0.9]">
            I'm not perfect.<br /><span className="text-brand">I'm human.</span>
          </h2>
          <div className="mt-6 h-1 w-20 bg-brand" />

          <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
            {tags.map((t) => (
              <div key={t.label} className="flex items-center gap-2.5 text-white/85">
                <div className="w-8 h-8 grid place-items-center bg-brand/15 border border-brand/40">
                  <t.icon className="w-4 h-4 text-brand" />
                </div>
                <span className="text-sm">{t.label}.</span>
              </div>
            ))}
          </div>

          <p className="mt-8 text-white/75 leading-relaxed">
            I love walking, coffee, time with friends and family, and honestly, I've made plenty of excuses along the way.
          </p>
          <p className="mt-4 text-brand font-semibold leading-relaxed">
            I know what it feels like to put everyone else first.
          </p>
          <p className="mt-3 text-white uppercase tracking-wide text-sm font-semibold leading-relaxed">
            And I know what it feels like to lose yourself in the process.
          </p>

          <div className="mt-8 inline-flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.25em]">
            <Instagram className="w-4 h-4 text-brand" /> {INSTAGRAM}
          </div>
        </div>

        <div className="lg:col-span-6 reveal order-1 lg:order-2">
          <div className="relative">
            <div className="absolute -inset-4 brand-gradient opacity-25 blur-3xl" />
            <div className="relative aspect-square overflow-hidden border border-white/10">
              <img src={IMAGES.aboutFamily} alt="Sam Turner with family" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChapterWhy() {
  return (
    <section className="relative py-24 bg-[#0a0a0d] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 reveal">
          <div className="relative">
            <div className="absolute -inset-4 brand-gradient opacity-30 blur-3xl" />
            <div className="relative aspect-[4/5] overflow-hidden border-4 border-brand">
              <img src={IMAGES.samMirror} alt="Sam Turner" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
        <div className="lg:col-span-6 reveal">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 font-semibold">03 / 05</div>
          <div className="mt-3 text-[11px] tracking-[0.35em] uppercase text-brand font-bold">Why I became a coach</div>
          <h2 className="mt-4 font-display italic text-white uppercase text-4xl md:text-5xl leading-[0.95]">
            I didn't become a coach because I had it <span className="text-brand">all figured out</span>.
          </h2>
          <div className="mt-6 h-1 w-20 bg-brand" />
          <div className="mt-6 space-y-4 text-white/80 leading-relaxed">
            <p>My passion for fitness started long before I became qualified.</p>
            <p>Then I became a mum. Suddenly, my time wasn't completely mine anymore. My body had changed in ways I wasn't prepared for, and I didn't really know how to deal with it.</p>
            <p>I didn't want to ask for help.</p>
            <p className="text-white">So I asked myself:</p>
          </div>
          <blockquote className="mt-6 relative pl-5 border-l-4 border-brand">
            <p className="font-display italic text-white text-2xl md:text-3xl uppercase leading-tight">"How can I make this work?"</p>
          </blockquote>
          <p className="mt-6 text-white/80 leading-relaxed">
            That question led me to create child-friendly fitness classes, making fitness fit around real life.
          </p>
          <p className="mt-5 text-brand font-bold uppercase tracking-widest text-sm">
            10 years later, that is still my reason.
          </p>
        </div>
      </div>
    </section>
  );
}

function ChapterJourney() {
  return (
    <section className="relative py-24 bg-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 reveal order-2 lg:order-1">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 font-semibold">04 / 05</div>
          <div className="mt-3 text-[11px] tracking-[0.35em] uppercase text-brand font-bold">My Journey</div>
          <h2 className="mt-4 font-display italic text-white uppercase text-5xl md:text-6xl leading-[0.9]">
            From finding a way to <span className="text-brand">helping others</span> find theirs.
          </h2>
          <div className="mt-6 h-1 w-20 bg-brand" />
          <div className="mt-6 space-y-4 text-white/80 leading-relaxed">
            <p>With over 10 years of experience in the fitness industry, I've built my career around more than just training in the gym. As a qualified <span className="text-white font-semibold">Level 3 Personal Trainer</span> and <span className="text-white font-semibold">Nutrition Coach</span>, I train, coach and support people at all stages of their fitness journey, helping them build strength, confidence and healthier habits that last.</p>
            <p>Outside of the gym, I love bringing people together through group walks, sports events, challenges and social activities, because I believe fitness should be about creating a lifestyle and a community you genuinely enjoy being part of.</p>
            <p>For me, coaching is about meeting people where they are, giving them the knowledge, accountability and encouragement they need, and helping them become the strongest version of themselves, both physically and mentally.</p>
          </div>
        </div>
        <div className="lg:col-span-6 reveal order-1 lg:order-2">
          <div className="relative">
            <div className="absolute -inset-4 brand-gradient opacity-25 blur-3xl" />
            <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
              <img src={IMAGES.mumBaby2} alt="Mum & Baby class" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChapterBelief() {
  const beliefs = [
    'Find your true self and build confidence',
    'You don\'t have to earn your food',
    'You don\'t have to punish yourself for enjoying life',
    'Your worth isn\'t measured by the number on the scales',
  ];
  return (
    <section className="relative py-24 bg-[#0a0a0d] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 reveal">
          <div className="relative">
            <div className="absolute -inset-4 brand-gradient opacity-30 blur-3xl" />
            <div className="relative aspect-square overflow-hidden border border-white/10">
              <img src={IMAGES.aboutOutdoors} alt="Outdoor group" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
        <div className="lg:col-span-6 reveal">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/50 font-semibold">05 / 05</div>
          <div className="mt-3 text-[11px] tracking-[0.35em] uppercase text-brand font-bold">What I believe</div>
          <h2 className="mt-4 font-display italic text-white uppercase text-4xl md:text-5xl leading-[0.95]">
            Women don't need to <span className="text-brand">punish themselves</span>.
          </h2>
          <div className="mt-6 h-1 w-20 bg-brand" />

          <ul className="mt-8 space-y-3">
            {beliefs.map((b) => (
              <li key={b} className="flex items-start gap-3 text-white/85">
                <Heart className="w-4 h-4 mt-1 text-brand fill-brand flex-shrink-0" />
                <span>{b}.</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 inline-flex items-center gap-3 bg-brand text-white px-5 py-3 uppercase tracking-widest text-xs font-bold">
            <Scale className="w-4 h-4" /> Strength matters more than scales
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-5 text-sm text-white/75 leading-relaxed">
            <p>I have body insecurities still, no matter if I'm doing fat loss or maintaining, they're still there. But now nearly 40, I have realised it's not my body I need to change, it's my mentality.</p>
            <p>My coaching isn't just about the physical change. It's about proving to yourself that you can do hard things, bringing out the best in my clients, showing them that changes in lifestyle will always win.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutCTA() {
  return (
    <section className="relative py-24 bg-black overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img src={IMAGES.warrior} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
      </div>
      <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center reveal">
        <div className="text-[11px] tracking-[0.3em] uppercase text-brand font-bold">Your turn</div>
        <h2 className="mt-4 font-display italic text-white uppercase text-5xl md:text-6xl leading-[0.9]">Let's <span className="text-brand">build yours</span>.</h2>
        <p className="mt-5 text-white/70 max-w-xl mx-auto">Whether it's your first class or your next chapter, I'll meet you where you are and build the plan around your life.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/contact"><Button className="brand-gradient text-white uppercase tracking-widest text-xs rounded-none px-7 py-6 glow-brand">Message Sam <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          <Link to="/transform"><Button variant="outline" className="bg-transparent border-white/25 text-white hover:bg-white hover:text-black uppercase tracking-widest text-xs rounded-none px-7 py-6">See Packages</Button></Link>
        </div>
        <a href={`https://instagram.com/${INSTAGRAM.replace('@','')}`} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 text-white/50 hover:text-brand text-xs uppercase tracking-[0.25em] transition-colors">
          <Instagram className="w-4 h-4" /> {INSTAGRAM}
        </a>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <main>
      <ChapterHero />
      <ChapterHuman />
      <ChapterWhy />
      <ChapterJourney />
      <ChapterBelief />
      <AboutCTA />
    </main>
  );
}
